/**
 * Route-level permission resolver.
 *
 * Reuses the SAME permission matrix as the sidebar menu (utils/permissions.js)
 * so that a user who cannot see a menu entry cannot open its pages via a
 * remembered/direct URL either. The guard matches on the FIRST URL segment,
 * so detail/add/edit sub-routes inherit the permission of their section
 * (/machines/:id/edit -> "machines").
 *
 * Convention: the feature key in permissions.js equals the first URL segment.
 * When a new page follows that convention it is guarded automatically; if the
 * segment and the feature key differ, add an entry to SEGMENT_FEATURE_ALIASES.
 * Segments whose access does not map to a single feature (promoter/training
 * flows) live in SPECIAL_SEGMENTS as predicates.
 *
 * Segments with NO case in permissions.js resolve to `false` for everyone
 * except ADMIN/MASTER (which bypasses the matrix) — secure by default.
 */

import { canAccess, hasAccessToAny } from "./permissions";

// Always reachable by any authenticated user (home + auth flows).
const OPEN_SEGMENTS = new Set([
  "", // HomeView
  "login",
  "passwordrecovery",
  "passwordassign",
  "passwordrecoveryclient",
  "passwordassignclient",
]);

// First URL segment -> feature key in permissions.js (only when they differ).
const SEGMENT_FEATURE_ALIASES = {
  shiftsadmin: "shiftsAdmin",
  arqueossales: "arqueosSales",
  "support-chat": "supportchat",
  "pro-dashboard": "arqueos", // menu gates the dashboard behind 'arqueos'
  user: "shiftsAdmin", // UserShiftView, linked from ShiftsAdminView
};

// Segments whose access rule is not a single feature (mirrors AppShell logic).
const SPECIAL_SEGMENTS = {
  // Promoter clock-in view: promoters land here from HomeView; admins that
  // manage promoters may open it too.
  promoter: (role, type, category) =>
    role === "PROMOTER" || canAccess(role, type, category, "promoters"),

  // Training courses: same rule as the menu's hasTraining flag.
  training: (role, type, category) =>
    role === "PROMOTER" ||
    hasAccessToAny(role, type, category, ["promoters", "users", "logistics"]),

  // Course management: same rule as the menu's hasAdmin flag.
  "training-admin": (role, type, category) =>
    hasAccessToAny(role, type, category, ["promoters", "users", "logistics"]),
};

export function featureForPath(pathname) {
  const segment = (pathname.split("/")[1] || "").toLowerCase();
  return SEGMENT_FEATURE_ALIASES[segment] || segment;
}

/**
 * Decide whether the current user may open a URL.
 * @returns {{allowed: boolean, reason?: "forbidden"}}
 */
export function checkRouteAccess(role, type, category, pathname) {
  const segment = (pathname.split("/")[1] || "").toLowerCase();
  if (OPEN_SEGMENTS.has(segment)) return { allowed: true };

  const special = SPECIAL_SEGMENTS[segment];
  if (special) {
    return special(role, type, category)
      ? { allowed: true }
      : { allowed: false, reason: "forbidden" };
  }

  const feature = SEGMENT_FEATURE_ALIASES[segment] || segment;
  if (!canAccess(role, type, category, feature)) {
    return { allowed: false, reason: "forbidden" };
  }
  return { allowed: true };
}

export default checkRouteAccess;
